import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { format, parseISO } from "date-fns";
import { Calendar, Clock, Check, X, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";

interface AppointmentRequestsProps {
  clinicianId: string;
  clients: any[];
  onSessionCreated?: () => void;
}

interface AppointmentRequest {
  id: string;
  client_id: string;
  request_type: string;
  existing_session_id: string | null;
  requested_date: string | null;
  requested_time: string | null;
  requested_duration: number | null;
  reason: string | null;
  status: string;
  created_at: string;
}

export function AppointmentRequests({ clinicianId, clients, onSessionCreated }: AppointmentRequestsProps) {
  const [requests, setRequests] = useState<AppointmentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [respondingTo, setRespondingTo] = useState<AppointmentRequest | null>(null);
  const [response, setResponse] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadRequests();

    const channel = supabase
      .channel('appointment-requests')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'appointment_requests',
          filter: `clinician_id=eq.${clinicianId}`
        },
        () => loadRequests()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [clinicianId]);

  const loadRequests = async () => {
    const { data, error } = await supabase
      .from('appointment_requests')
      .select('*')
      .eq('clinician_id', clinicianId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading requests:', error);
      setLoading(false);
      return;
    }

    setRequests(data || []);
    setLoading(false);
  };

  const getClientName = (clientId: string) => {
    const client = clients.find(c => c.client_id === clientId);
    return client?.profiles?.full_name || client?.profiles?.email || 'Unknown Client';
  };

  const handleApprove = async (request: AppointmentRequest) => {
    setProcessing(true);

    // If it's a new appointment request, create the session
    if (request.request_type === 'new' && request.requested_date) {
      const clinicianClient = clients.find(c => c.client_id === request.client_id);
      
      const { error: sessionError } = await supabase
        .from('therapy_sessions')
        .insert({
          clinician_id: clinicianId,
          client_id: request.client_id,
          clinician_client_id: clinicianClient?.id,
          session_date: request.requested_date,
          session_time: request.requested_time || '10:00',
          duration_minutes: request.requested_duration || 50,
          status: 'scheduled',
          session_type: 'individual'
        } as any);


      if (sessionError) {
        console.error('Error creating session:', sessionError);
        toast.error('Failed to create session');
        setProcessing(false);
        return;
      }
    }

    // If it's a reschedule, update the existing session
    if (request.request_type === 'reschedule' && request.existing_session_id && request.requested_date) {
      const { error: updateError } = await supabase
        .from('therapy_sessions')
        .update({
          session_date: request.requested_date,
          session_time: request.requested_time || '10:00'
        })
        .eq('id', request.existing_session_id);

      if (updateError) {
        console.error('Error updating session:', updateError);
        toast.error('Failed to reschedule session');
        setProcessing(false);
        return;
      }
    }

    // If it's a cancellation, cancel the session
    if (request.request_type === 'cancel' && request.existing_session_id) {
      const { error: cancelError } = await supabase
        .from('therapy_sessions')
        .update({ status: 'cancelled' })
        .eq('id', request.existing_session_id);

      if (cancelError) {
        console.error('Error cancelling session:', cancelError);
        toast.error('Failed to cancel session');
        setProcessing(false);
        return;
      }
    }

    // Update the request status
    const { error } = await supabase
      .from('appointment_requests')
      .update({
        status: 'approved',
        clinician_response: response || null,
        responded_at: new Date().toISOString()
      })
      .eq('id', request.id);

    if (error) {
      console.error('Error updating request:', error);
      toast.error('Failed to approve request');
      setProcessing(false);
      return;
    }

    toast.success('Request approved');
    setRespondingTo(null);
    setResponse("");
    setProcessing(false);
    loadRequests();
    onSessionCreated?.();
  };

  const handleDecline = async (request: AppointmentRequest) => {
    if (!response.trim()) {
      toast.error('Please provide a reason for declining');
      return;
    }

    setProcessing(true);
    const { error } = await supabase
      .from('appointment_requests')
      .update({
        status: 'declined',
        clinician_response: response,
        responded_at: new Date().toISOString()
      })
      .eq('id', request.id);

    if (error) {
      console.error('Error updating request:', error);
      toast.error('Failed to decline request');
      setProcessing(false);
      return;
    }

    toast.success('Request declined');
    setRespondingTo(null);
    setResponse("");
    setProcessing(false);
    loadRequests();
  };

  const getRequestIcon = (type: string) => {
    switch (type) {
      case 'new':
        return <Calendar className="h-4 w-4 text-green-500" />;
      case 'reschedule':
        return <RefreshCw className="h-4 w-4 text-blue-500" />;
      case 'cancel':
        return <X className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Check className="h-12 w-12 mx-auto mb-4 text-green-500 opacity-50" />
          <p className="text-muted-foreground">No pending appointment requests</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-card/50 border-border/30">
        <CardHeader>
          <CardTitle className="flex items-centre gap-2 text-foreground">
            <AlertCircle className="h-5 w-5 text-primary" />
            Pending Requests
            <Badge variant="secondary" className="bg-primary/20 text-primary">{requests.length}</Badge>
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Client appointment requests awaiting your response
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {requests.map((request) => (
              <div
                key={request.id}
                className="p-4 border border-border rounded-lg bg-card"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    {getRequestIcon(request.request_type)}
                    <div>
                      <p className="font-medium">{getClientName(request.client_id)}</p>
                      <p className="text-sm text-muted-foreground capitalize">
                        {request.request_type === 'new' ? 'New Appointment' : 
                         request.request_type === 'reschedule' ? 'Reschedule Request' :
                         'Cancellation Request'}
                      </p>
                      {request.requested_date && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <Clock className="h-3 w-3" />
                          {format(parseISO(request.requested_date), 'PPP')} at {request.requested_time}
                          {request.requested_duration && ` · ${request.requested_duration} min`}
                        </p>
                      )}
                      {request.reason && (
                        <p className="text-sm mt-2 p-2 bg-muted/50 rounded">
                          "{request.reason}"
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setRespondingTo(request);
                        setResponse("");
                      }}
                    >
                      Respond
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!respondingTo} onOpenChange={(open) => !open && setRespondingTo(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Respond to Request</DialogTitle>
            <DialogDescription>
              {respondingTo?.request_type === 'new' ? 'New appointment request' :
               respondingTo?.request_type === 'reschedule' ? 'Reschedule request' :
               'Cancellation request'} from {respondingTo && getClientName(respondingTo.client_id)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {respondingTo?.requested_date && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium">Requested Time</p>
                <p className="text-sm text-muted-foreground">
                  {format(parseISO(respondingTo.requested_date), 'PPPP')} at {respondingTo.requested_time}
                </p>
              </div>
            )}
            {respondingTo?.reason && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium">Client's Note</p>
                <p className="text-sm text-muted-foreground">{respondingTo.reason}</p>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Your Response (optional for approve, required for decline)</label>
              <Textarea
                placeholder="Add a message for the client..."
                value={response}
                onChange={(e) => setResponse(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline" disabled={processing}>Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={() => respondingTo && handleDecline(respondingTo)}
              disabled={processing}
            >
              <X className="h-4 w-4 mr-1" />
              Decline
            </Button>
            <Button
              onClick={() => respondingTo && handleApprove(respondingTo)}
              disabled={processing}
            >
              <Check className="h-4 w-4 mr-1" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
